import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, MessageCircle, ArrowLeft, Share2, Send } from 'lucide-react';
import { API } from '../utils/api';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import CommunityBottomNav from '../components/community/CommunityBottomNav';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import { formatDate, TEAL_ACCENT } from '../utils/homeHelpers';
import notify from '../utils/notify';

const COMM = `${API}/community`;

const Shell = ({ children }) => (
  <div style={{ background: '#F5F7FA', minHeight: '100vh', paddingBottom: 80 }}>
    <CommunityTopHeader />
    <div style={{ width: '100%', maxWidth: 380, margin: '0 auto', padding: '0 16px' }}>
      {children}
    </div>
    <CommunityBottomNav active="home" />
  </div>
);

const BackBar = ({ navigate }) => (
  <button
    onClick={() => navigate('/community')}
    data-testid="community-post-detail-back"
    style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '12px 0',
      background: 'transparent', border: 'none', color: TEAL_ACCENT,
      fontSize: 14, fontWeight: 600, cursor: 'pointer',
    }}
  >
    <ArrowLeft size={16} /> Back to feed
  </button>
);

const initialsOf = (name) =>
  (name || 'U').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

const CommentItem = ({ comment }) => (
  <div
    data-testid={`community-post-detail-comment-${comment.id}`}
    style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid #F3F4F6' }}
  >
    <div style={{
      width: 32, height: 32, borderRadius: '50%', background: '#F3F4F6',
      border: `2px solid ${TEAL_ACCENT}30`, display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: TEAL_ACCENT, fontWeight: 600, fontSize: 11,
      flexShrink: 0,
    }}>
      {initialsOf(comment.username)}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
          {comment.username || 'Community Member'}
        </span>
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>{formatDate(comment.created_at)}</span>
      </div>
      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginTop: 2, whiteSpace: 'pre-wrap' }}>
        {comment.content}
      </div>
    </div>
  </div>
);

const CommunityPostDetailPage = () => {
  const { postId: rawPostId } = useParams();
  // Strip the carousel-wrapper prefix some legacy share URLs may include
  // (e.g. `/community/posts/post_<uuid>`).
  const postId = (rawPostId || '').replace(/^post_/, '');
  const navigate = useNavigate();
  const { user, token } = useCommunityAuth();

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [liked, setLiked] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setErrorMsg('');
      try {
        const res = await axios.get(`${COMM}/posts/${postId}`, authHeaders);
        if (cancelled) return;
        setPost(res.data);
        setComments(Array.isArray(res.data?.comments) ? res.data.comments : []);
        setLiked(Array.isArray(res.data?.likes) && !!user && res.data.likes.includes(user.id));
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          setErrorMsg(err?.response?.data?.detail || 'Unable to load this post. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (postId && token) load();
    return () => { cancelled = true; };
  }, [postId, token, user, authHeaders]);

  const handleLike = async () => {
    if (!post || likeBusy) return;
    setLikeBusy(true);
    try {
      const r = await axios.post(`${COMM}/posts/${post.id}/like`, {}, authHeaders);
      setLiked(!!r.data?.liked);
      setPost(prev => prev ? { ...prev, likes_count: r.data?.total_likes ?? prev.likes_count } : prev);
    } catch {
      // best-effort UX action; ignore errors silently
    } finally {
      setLikeBusy(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = { title: 'Check out this post on SocialStar Community!', text: post?.content || '', url };
    try {
      if (navigator.share) await navigator.share(shareData);
      else { await navigator.clipboard.writeText(url); notify.success('Link copied to clipboard'); }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        try { await navigator.clipboard.writeText(url); notify.success('Link copied'); } catch { /* ignore */ }
      }
    }
  };

  const handleSubmitComment = async (e) => {
    e?.preventDefault?.();
    const text = commentText.trim();
    if (!text || !post || submitting) return;
    setSubmitting(true);
    try {
      // POST /api/community/posts/{post_id}/comment — auth header is
      // attached explicitly via authHeaders (community uses a separate
      // token stored under localStorage.community_token).
      const res = await axios.post(
        `${COMM}/posts/${post.id}/comment`,
        { content: text },
        authHeaders
      );
      const newComment = res.data;
      setComments(prev => [...prev, newComment]);
      setPost(prev => prev ? { ...prev, comments_count: (prev.comments_count || 0) + 1 } : prev);
      setCommentText('');
    } catch (err) {
      notify.error(err?.response?.data?.detail || 'Failed to post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <BackBar navigate={navigate} />
        <div data-testid="community-post-detail-loading" style={{ padding: '40px 16px', textAlign: 'center', color: '#6B7280' }}>
          Loading post…
        </div>
      </Shell>
    );
  }

  if (notFound) {
    return (
      <Shell>
        <BackBar navigate={navigate} />
        <div
          data-testid="community-post-detail-not-found"
          style={{
            background: '#FFFFFF', borderRadius: 16, padding: 28, marginTop: 12,
            textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1F2937', marginBottom: 8 }}>
            Post not found
          </div>
          <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.5, marginBottom: 20 }}>
            This post may have been removed.
          </div>
          <button
            onClick={() => navigate('/community')}
            data-testid="community-post-detail-go-home"
            style={{
              padding: '12px 22px', background: TEAL_ACCENT, color: '#FFFFFF',
              border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Go to home feed
          </button>
        </div>
      </Shell>
    );
  }

  if (errorMsg) {
    return (
      <Shell>
        <BackBar navigate={navigate} />
        <div
          data-testid="community-post-detail-error"
          style={{
            background: '#FEF2F2', borderRadius: 12, padding: 16, marginTop: 12,
            color: '#991B1B', fontSize: 14,
          }}
        >
          {errorMsg}
        </div>
      </Shell>
    );
  }

  if (!post) return <Shell><BackBar navigate={navigate} /></Shell>;

  const imageUrl = post.media_url
    ? (post.media_url.startsWith('/api') ? `${API.replace('/api', '')}${post.media_url}` : post.media_url)
    : null;
  const likesTotal = post.likes_count ?? (post.likes?.length || 0);
  const commentsTotal = post.comments_count ?? comments.length;

  return (
    <Shell>
      <BackBar navigate={navigate} />

      <article
        data-testid="community-post-detail-card"
        style={{
          background: '#FFFFFF', borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: TEAL_ACCENT, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Community Post</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px 8px' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: '#F3F4F6',
            border: `2px solid ${TEAL_ACCENT}30`, display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: TEAL_ACCENT, fontWeight: 600, fontSize: 14,
          }}>
            {initialsOf(post.username)}
          </div>
          <div style={{ flex: 1 }}>
            <div data-testid="community-post-detail-author" style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
              {post.username || 'Community Member'}
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>{formatDate(post.created_at)}</div>
          </div>
        </div>

        <div style={{ padding: '4px 16px 12px' }}>
          <p data-testid="community-post-detail-content" style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
            {post.content}
          </p>
          {Array.isArray(post.tags) && post.tags.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {post.tags.map((tag, i) => (
                <span key={i} style={{ color: TEAL_ACCENT, fontSize: 12, fontWeight: 500 }}>
                  #{String(tag).replace(/^#/, '')}
                </span>
              ))}
            </div>
          )}
        </div>

        {imageUrl && (
          <div style={{ background: '#F9FAFB' }}>
            <img
              src={imageUrl}
              alt=""
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
              style={{ width: '100%', display: 'block', maxHeight: 480, objectFit: 'cover' }}
              data-testid="community-post-detail-image"
            />
          </div>
        )}

        <div style={{
          display: 'flex', alignItems: 'center', gap: 18,
          padding: '12px 16px', borderTop: '1px solid rgba(0,0,0,0.05)',
        }}>
          <button
            onClick={handleLike}
            disabled={likeBusy}
            data-testid="community-post-detail-like-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 6, background: 'transparent',
              border: 'none', padding: 0, cursor: likeBusy ? 'default' : 'pointer',
              color: liked ? '#DC2626' : '#6B7280', fontSize: 13, fontWeight: 500,
            }}
          >
            <Heart size={18} fill={liked ? '#DC2626' : 'none'} />
            <span data-testid="community-post-detail-likes-count">{likesTotal}</span>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: 13, fontWeight: 500 }}>
            <MessageCircle size={18} />
            <span data-testid="community-post-detail-comments-count">{commentsTotal}</span>
          </div>
          <button
            onClick={handleShare}
            data-testid="community-post-detail-share-btn"
            style={{
              marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              color: '#6B7280', fontSize: 13, fontWeight: 500,
            }}
          >
            <Share2 size={18} /> Share
          </button>
        </div>
      </article>

      <section
        data-testid="community-post-detail-comments-section"
        style={{
          background: '#FFFFFF', borderRadius: 16, padding: '12px 16px', marginTop: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937', padding: '4px 0 8px' }}>
          Comments ({commentsTotal})
        </div>

        <div data-testid="community-post-detail-comments-list">
          {comments.length === 0 ? (
            <div style={{ padding: '12px 0', fontSize: 13, color: '#9CA3AF' }}>
              Be the first to comment.
            </div>
          ) : (
            comments.map((c) => <CommentItem key={c.id} comment={c} />)
          )}
        </div>

        <form
          onSubmit={handleSubmitComment}
          style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 12 }}
        >
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Write a comment…"
            data-testid="community-post-detail-comment-input"
            disabled={submitting}
            style={{
              flex: 1, padding: '10px 12px', borderRadius: 10,
              border: '1px solid #E5E7EB', fontSize: 14, outline: 'none',
              background: '#F9FAFB',
            }}
          />
          <button
            type="submit"
            disabled={submitting || !commentText.trim()}
            data-testid="community-post-detail-comment-submit"
            style={{
              padding: '10px 12px', borderRadius: 10, border: 'none',
              background: TEAL_ACCENT, color: '#FFFFFF', cursor: (submitting || !commentText.trim()) ? 'default' : 'pointer',
              opacity: (submitting || !commentText.trim()) ? 0.6 : 1,
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
            }}
          >
            <Send size={14} /> {submitting ? 'Posting…' : 'Post'}
          </button>
        </form>
      </section>
    </Shell>
  );
};

export default CommunityPostDetailPage;
